import type {
  FieldErrors,
  FieldValues,
  Path,
  UseFormRegister,
  UseFormTrigger,
} from 'react-hook-form';

import InputErrorText from '@/components/InputErrorText/InputErrorText';
import { type Event, trackEvents } from '@/utils/analytics';

import useStyles from './RadioGroup.styles';

type RadioOption = {
  value: string;
  label?: string;
};

type RadioGroupProps<T extends FieldValues> = {
  name: Path<T>;
  options: RadioOption[];
  register: UseFormRegister<T>;
  trigger: UseFormTrigger<T>;
  legend?: string;
  errors?: FieldErrors<T>;
  className?: string;
  containerClassName?: string;
  isOnDark?: boolean;
  gaEvent?: Event;
  isSmall?: boolean;
  testId?: string;
};

export function RadioGroup<T extends FieldValues>({
  options,
  name,
  register,
  trigger,
  legend,
  errors,
  testId,
  className,
  containerClassName,
  gaEvent,
  isOnDark = false,
  isSmall = false,
}: RadioGroupProps<T>) {
  const { classes, cx } = useStyles({ isOnDark });

  const { onChange: hookOnChange, ...radioRegister } = register(name);
  function handleClick(
    e: React.ChangeEvent<HTMLInputElement>,
    label: string | undefined
  ) {
    hookOnChange(e);
    trigger(name);
    if (gaEvent && label) {
      trackEvents({
        ...gaEvent,
        click_text: label,
      });
    }
  }

  return (
    <fieldset className={cx(classes.fieldset, className, containerClassName)}>
      {legend && (
        <legend className={cx(isSmall ? classes.legendSmall : classes.legend)}>
          {legend}
        </legend>
      )}
      <div className={cx(classes.radioButtonsContainer, containerClassName)}>
        {options.map((option) => (
          <div
            className={cx(classes.radio, containerClassName)}
            key={option.value}
          >
            <input
              // biome-ignore lint/complexity/useLiteralKeys: Automated migration to Biome
              {...(testId && { ['data-testid']: `${testId}-${option.value}` })}
              className={classes.radioInput}
              {...radioRegister}
              type='radio'
              value={option.value}
              id={option.value}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleClick(e, option.label)
              }
            />
            <label className={classes.label} htmlFor={option.value}>
              {option.label}
            </label>
          </div>
        ))}
      </div>
      <InputErrorText errorMessage={errors?.[name]?.message?.toString()} />
    </fieldset>
  );
}
